import { Component, computed, DestroyRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription, switchMap, tap } from 'rxjs';
import { Button } from 'primeng/button';
import { Message } from 'primeng/message';
import { Skeleton } from 'primeng/skeleton';
import { TherapistStateService } from '@therapist/shared/services/state/therapist-state.service';
import { SubscriptionApiService } from '@therapist/shared/services/api/subscription-api.service';
import { TherapistAccessStateEnum } from '@therapist/shared/models/subscription/therapist-access-state.enum';
import { SubscriptionPlanEnum } from '@therapist/shared/models/subscription/subscription-plan.enum';
import { SubscriptionOverviewDto } from '@therapist/shared/models/subscription/subscription-overview.dto';
import { SubscriptionPlanTranslationsPipe } from '@therapist/shared/pipes/subscription-plan-translations/subscription-plan-translations.pipe';
import { PAYMENT_OVERDUE_MESSAGE } from '@therapist/shared/components/subscription-banner/subscription-banner.component';
import { SUPPORT_EMAIL } from '../../../../shared/constants/support.const';

type PageState = 'loading' | 'error' | 'ready' | 'confirming' | 'processing' | 'confirm-error';

interface PlanCard {
  plan: SubscriptionPlanEnum;
  price: string;
  details: string;
}

/** Code im 409-Body, wenn für das Institut bereits ein zugangsgewährendes Abo besteht. */
const SUBSCRIPTION_ALREADY_ACTIVE_CODE = 'subscription-already-active';

@Component({
  selector: 'sana-tp-subscription-page',
  imports: [
    DatePipe,
    Button,
    Message,
    Skeleton,
    SubscriptionPlanTranslationsPipe
  ],
  templateUrl: './subscription-page.component.html',
  styleUrl: './subscription-page.component.scss'
})
export class SubscriptionPageComponent implements OnInit {

  readonly #therapistState = inject(TherapistStateService);
  readonly #api = inject(SubscriptionApiService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly TherapistAccessStateEnum = TherapistAccessStateEnum;
  protected readonly supportEmail = SUPPORT_EMAIL;
  protected readonly paymentOverdueMessage = PAYMENT_OVERDUE_MESSAGE;

  // Preise fest hinterlegt (netto), siehe Spec; Preis-IDs liegen in der Backend-Konfiguration
  protected readonly planCards: PlanCard[] = [
    {
      plan: SubscriptionPlanEnum.SinglePracticeMonthly,
      price: '59,99 € / Monat',
      details: 'zzgl. MwSt., monatliche Abrechnung'
    },
    {
      plan: SubscriptionPlanEnum.SinglePracticeYearly,
      price: '49,99 € / Monat',
      details: 'zzgl. MwSt., jährliche Abrechnung: 599,88 € im Voraus'
    },
    {
      plan: SubscriptionPlanEnum.GroupPractice,
      price: '339,99 € / Monat',
      details: 'zzgl. MwSt., monatliche Abrechnung, für mehrere Therapeut:innen eines Instituts'
    }
  ];

  protected readonly accessState = this.#therapistState.accessState;
  protected readonly testAccountEndsAt = computed(() => this.#therapistState.currentTherapist()?.testAccountEndsAt);
  protected readonly pageState = signal<PageState>('loading');
  protected readonly overview = signal<SubscriptionOverviewDto | null>(null);
  protected readonly subscription = computed(() => this.overview()?.subscription);
  /** Plan, für den gerade eine Checkout-Session angelegt wird; bleibt bis zur Weiterleitung gesetzt. */
  protected readonly checkoutPlan = signal<SubscriptionPlanEnum | null>(null);
  protected readonly isRedirecting = computed(() => this.checkoutPlan() !== null);
  protected readonly notice = signal<string | null>(null);      // Hinweise (abgebrochen, bereits aktiv, Erfolg)
  protected readonly checkoutError = signal<string | null>(null);
  protected readonly showPlans = computed(() =>
    this.pageState() === 'ready'
    && (this.accessState() === TherapistAccessStateEnum.Locked || this.accessState() === TherapistAccessStateEnum.Trial));

  #sessionId: string | null = null;
  /** Laufender Seitenaufbau bzw. Confirm; ein neuer Aufruf bricht den vorherigen ab, damit kein veraltetes Ergebnis den Zustand überschreibt. */
  #pageRequest: Subscription | null = null;

  ngOnInit(): void {
    // Die Komponente wird bei geänderten Query-Parametern wiederverwendet (Rückkehr von Stripe, Entfernen der Parameter)
    this.#route.queryParamMap.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(params => {
      const checkout = params.get('checkout');
      const sessionId = params.get('session_id');

      if (checkout === 'success' && sessionId) {
        this.#sessionId = sessionId;
        this.confirm();
      } else {
        if (checkout === 'cancelled') {
          this.notice.set('Abschluss abgebrochen');
        }
        this.loadOverview();
      }
    });
  }

  /** Browser-Zurück von Stripe stellt die Seite aus dem Back/Forward-Cache wieder her; die Buttons wären sonst weiter deaktiviert. */
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.checkoutPlan.set(null);
    }
  }

  /** Einzige Stelle, die die Seite verlässt; im Spec ersetzt. */
  redirectTo(url: string): void {
    window.location.href = url;
  }

  protected loadOverview(): void {
    this.pageState.set('loading');

    this.#pageRequest?.unsubscribe();
    this.#pageRequest = forkJoin([this.#therapistState.loadTherapistProfile$(), this.#api.getOverview()])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: ([, overview]) => {
          this.overview.set(overview);
          this.pageState.set('ready');
        },
        error: () => this.pageState.set('error')
      });
  }

  startCheckout(plan: SubscriptionPlanEnum): void {
    this.checkoutPlan.set(plan);
    this.checkoutError.set(null);

    this.#api.createCheckout(plan).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: response => this.redirectTo(response.url),
      error: (error: HttpErrorResponse) => {
        this.checkoutPlan.set(null);

        // ein Kollege hat inzwischen für das Institut abgeschlossen
        if (error.status === 409 && error.error?.code === SUBSCRIPTION_ALREADY_ACTIVE_CODE) {
          this.notice.set('Für Ihre Praxis besteht bereits ein aktives Abo.');
          this.loadOverview();
          return;
        }

        this.checkoutError.set(`Der Abschluss ist derzeit nicht möglich. Bitte wenden Sie sich an ${SUPPORT_EMAIL}.`);
      }
    });
  }

  confirm(): void {
    this.pageState.set('confirming');

    this.#pageRequest?.unsubscribe();
    this.#pageRequest = this.#api.confirmCheckout(this.#sessionId!)
      .pipe(
        tap(overview => this.overview.set(overview)),
        switchMap(() => this.#therapistState.loadTherapistProfile$()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => {
          const accessState = this.accessState();

          // Webhook oder Confirm hat das Abo bereits übernommen
          if (accessState === TherapistAccessStateEnum.Subscribed || accessState === TherapistAccessStateEnum.PaymentOverdue) {
            this.notice.set('Ihr Abo ist aktiv. Vielen Dank!');
            this.pageState.set('ready');
            this.#removeQueryParams();
            return;
          }

          // Stripe hat die Zahlung noch nicht bestätigt; Parameter bleiben für "Erneut prüfen"
          this.pageState.set('processing');
        },
        error: (error: HttpErrorResponse) => {
          // Session unbekannt oder fremd: die Parameter-Änderung lädt die normale Ansicht
          if (error.status === 404) {
            this.#removeQueryParams();
            return;
          }

          this.pageState.set('confirm-error');
        }
      });
  }

  #removeQueryParams(): void {
    this.#router.navigate([], { relativeTo: this.#route, queryParams: {}, replaceUrl: true });
  }
}
